import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Edit, Trash2, User, Users, Building2, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

export default function TeamSection({ eventId, permissions }: { eventId: string; permissions: any }) {
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'event' | 'client'>('all');
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [formData, setFormData] = useState<any>({
    category: 'event',
    first_name: '',
    last_name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    mobile: '',
    avatar_url: '',
    notes: ''
  });

  useEffect(() => {
    loadTeamMembers();
  }, [eventId]);

  const loadTeamMembers = async () => {
    const { data } = await supabase
      .from('team_members')
      .select('*')
      .eq('event_id', eventId)
      .order('category', { ascending: true })
      .order('last_name', { ascending: true });
    if (data) setTeamMembers(data);
    setLoading(false);
  };

  const handleAddNew = () => {
    setFormData({
      category: 'event',
      first_name: '',
      last_name: '',
      title: '',
      company: '',
      email: '',
      phone: '',
      mobile: '',
      avatar_url: '',
      notes: ''
    });
    setPhotoPreview(null);
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (member: any) => {
    setFormData({
      category: member.category || 'event',
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      title: member.title || '',
      company: member.company || '',
      email: member.email || '',
      phone: member.phone || '',
      mobile: member.mobile || '',
      avatar_url: member.avatar_url || '',
      notes: member.notes || ''
    });
    setPhotoPreview(member.avatar_url || null);
    setEditingId(member.id);
    setShowForm(true);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }

      setUploading(true);

      const fileExt = file.name.split('.').pop();
      const fileName = `${eventId}/${Date.now()}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('team-photos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) {
        if (uploadError.message.includes('not found')) {
          const { error: bucketError } = await supabase.storage.createBucket('team-photos', {
            public: true,
            fileSizeLimit: 5242880,
          });

          if (bucketError) throw bucketError;

          const { data: retryData, error: retryError } = await supabase.storage
            .from('team-photos')
            .upload(fileName, file, { upsert: true });

          if (retryError) throw retryError;
          if (!retryData) throw new Error('Upload failed');

          const { data: { publicUrl } } = supabase.storage
            .from('team-photos')
            .getPublicUrl(retryData.path);

          setFormData({ ...formData, avatar_url: publicUrl });
          setPhotoPreview(publicUrl);
        } else {
          throw uploadError;
        }
      } else {
        const { data: { publicUrl } } = supabase.storage
          .from('team-photos')
          .getPublicUrl(uploadData.path);

        setFormData({ ...formData, avatar_url: publicUrl });
        setPhotoPreview(publicUrl);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Error uploading photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    setFormData({ ...formData, avatar_url: '' });
    setPhotoPreview(null);
  };

  const handleSave = async () => {
    try {
      const fullName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim();

      if (!fullName) {
        alert('Please enter at least a first or last name');
        return;
      }

      const cleanedData = {
        category: formData.category,
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        full_name: fullName,
        title: formData.title || null,
        company: formData.company || null,
        email: formData.email || null,
        phone: formData.phone || null,
        mobile: formData.mobile || null,
        avatar_url: formData.avatar_url || null,
        notes: formData.notes || null
      };

      if (editingId) {
        await supabase
          .from('team_members')
          .update(cleanedData)
          .eq('id', editingId);
      } else {
        await supabase
          .from('team_members')
          .insert({ ...cleanedData, event_id: eventId });
      }
      await loadTeamMembers();
      setShowForm(false);
      setEditingId(null);
    } catch (error) {
      console.error('Error saving team member:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await supabase.from('team_members').delete().eq('id', deleteId);
      await loadTeamMembers();
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting team member:', error);
    }
  };

  const filteredMembers = teamMembers.filter(member =>
    selectedCategory === 'all' || member.category === selectedCategory
  );

  const eventTeam = teamMembers.filter(m => m.category === 'event');
  const clientTeam = teamMembers.filter(m => m.category === 'client');

  if (loading) {
    return <div className="text-center py-8 text-zinc-500">Loading team...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-white">Team</h2>
        {permissions.canEdit && (
          <Button
            onClick={handleAddNew}
            className="bg-primary-500 hover:bg-primary-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Team Member
          </Button>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-lg border transition-colors ${
            selectedCategory === 'all'
              ? 'bg-primary-500 border-primary-500 text-white'
              : 'bg-zinc-900/50 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-600'
          }`}
        >
          All ({teamMembers.length})
        </button>
        <button
          onClick={() => setSelectedCategory('event')}
          className={`px-4 py-2 rounded-lg border transition-colors ${
            selectedCategory === 'event'
              ? 'bg-primary-500 border-primary-500 text-white'
              : 'bg-zinc-900/50 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-600'
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Event Team ({eventTeam.length})
        </button>
        <button
          onClick={() => setSelectedCategory('client')}
          className={`px-4 py-2 rounded-lg border transition-colors ${
            selectedCategory === 'client'
              ? 'bg-primary-500 border-primary-500 text-white'
              : 'bg-zinc-900/50 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-zinc-600'
          }`}
        >
          <Building2 className="w-4 h-4 inline mr-2" />
          Client Team ({clientTeam.length})
        </button>
      </div>

      {filteredMembers.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-12 text-center">
          <p className="text-zinc-500">
            {teamMembers.length === 0 ? 'No team members yet' : 'No members in this category'}
          </p>
          {permissions.canEdit && teamMembers.length === 0 && (
            <Button
              onClick={handleAddNew}
              className="mt-4 bg-primary-500 hover:bg-primary-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Team Member
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMembers.map((member) => (
            <div
              key={member.id}
              className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6 hover:bg-zinc-800/50 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {member.avatar_url ? (
                    <img
                      src={member.avatar_url}
                      alt={member.full_name}
                      className="w-16 h-16 rounded-full object-cover bg-zinc-800"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center">
                      <User className="w-8 h-8 text-zinc-600" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">{member.full_name}</h3>
                      {member.title && (
                        <p className="text-sm text-zinc-400 truncate">{member.title}</p>
                      )}
                      {member.company && (
                        <p className="text-sm text-zinc-500 truncate">{member.company}</p>
                      )}
                    </div>
                    {permissions.canEdit && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEdit(member)}
                          className="text-primary-400 hover:text-primary-300 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteId(member.id)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 space-y-1">
                    {member.email && (
                      <p className="text-sm text-zinc-400 truncate">
                        <a href={`mailto:${member.email}`} className="hover:text-primary-400 transition-colors">
                          {member.email}
                        </a>
                      </p>
                    )}
                    {member.phone && (
                      <p className="text-sm text-zinc-400">
                        <a href={`tel:${member.phone}`} className="hover:text-primary-400 transition-colors">
                          {member.phone}
                        </a>
                      </p>
                    )}
                    {member.mobile && (
                      <p className="text-sm text-zinc-400">
                        <a href={`tel:${member.mobile}`} className="hover:text-primary-400 transition-colors">
                          {member.mobile} (Mobile)
                        </a>
                      </p>
                    )}
                  </div>

                  {member.notes && (
                    <p className="mt-2 text-xs text-zinc-500 line-clamp-2">{member.notes}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                {editingId ? 'Edit' : 'Add'} Team Member
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-zinc-400 hover:text-white"
              >
                <Plus className="w-5 h-5 rotate-45" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <Label className="text-zinc-300 text-sm">Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="event">Event Team</SelectItem>
                    <SelectItem value="client">Client Team</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-sm">First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-sm">Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Producer, Account Manager"
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Company</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Company name"
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-sm">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-sm">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="xxx.xxx.xxxx"
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Mobile</Label>
                  <Input
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="xxx.xxx.xxxx"
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-sm">Photo</Label>
                <div className="mt-1.5 space-y-3">
                  {photoPreview ? (
                    <div className="relative inline-block">
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="w-32 h-32 rounded-lg object-cover border border-zinc-700"
                      />
                      <button
                        type="button"
                        onClick={handleRemovePhoto}
                        className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:bg-zinc-800/50 transition-colors">
                      <div className="flex flex-col items-center justify-center py-4">
                        <Upload className="w-8 h-8 text-zinc-500 mb-2" />
                        <p className="text-sm text-zinc-400">
                          {uploading ? 'Uploading...' : 'Click to upload photo'}
                        </p>
                        <p className="text-xs text-zinc-500 mt-1">PNG, JPG up to 5MB</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-sm">Notes</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Additional notes..."
                  className="mt-1.5 w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSave}
                  className="flex-1 bg-primary-500 hover:bg-primary-600 text-white"
                >
                  {editingId ? 'Save Changes' : 'Add Team Member'}
                </Button>
                <Button
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Team Member?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove this team member from the event.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
